export type DeepLinkSource =
  | 'auth-guard'
  | 'wildcard-guard'
  | 'digital-payment-callback'
  | 'legacy-mapping'
  | 'unknown';

export interface DeepLinkPayload {
  readonly path: string;
  readonly query: string;
  readonly url: string;
  readonly source: DeepLinkSource;
  readonly createdAt: string;
}

export function createDeepLinkPayload(pathOrUrl: string, source: DeepLinkSource): DeepLinkPayload {
  const normalizedUrl = normalizeDeepLinkUrl(pathOrUrl);
  const queryStart = normalizedUrl.indexOf('?');
  const path = queryStart >= 0 ? normalizedUrl.substring(0, queryStart) : normalizedUrl;
  const query = queryStart >= 0 ? normalizedUrl.substring(queryStart) : '';

  return {
    path,
    query,
    url: normalizedUrl,
    source,
    createdAt: new Date().toISOString(),
  };
}

export function getDeepLinkUrl(value: unknown): string | null {
  if (typeof value === 'string') {
    return normalizeDeepLinkUrl(value);
  }

  if (isDeepLinkPayload(value)) {
    return normalizeDeepLinkUrl(value.url || `${value.path}${value.query}`);
  }

  return null;
}

export function getDeepLinkSource(value: unknown): DeepLinkSource {
  if (isDeepLinkPayload(value)) {
    return value.source;
  }

  if (typeof value === 'string') {
    return 'legacy-mapping';
  }

  return 'unknown';
}

export function isDeepLinkPayload(value: unknown): value is DeepLinkPayload {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const payload = value as Record<string, unknown>;
  return (
    typeof payload['path'] === 'string' &&
    typeof payload['query'] === 'string' &&
    typeof payload['url'] === 'string' &&
    typeof payload['source'] === 'string' &&
    typeof payload['createdAt'] === 'string'
  );
}

function normalizeDeepLinkUrl(pathOrUrl: string): string {
  const trimmed = pathOrUrl.trim();
  if (!trimmed) {
    return '/';
  }

  if (trimmed.startsWith('/')) {
    return trimmed;
  }

  return `/${trimmed}`;
}
